import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList } from 'react-native';
import DOMPurify from 'dompurify';
import LoadingWrapper from '../../components/utility/loading-wrapper';
import Placeholder from '../../components/utility/placeholder';
import COLORS from '../../constants/colors';
import { tKeys } from '../../translations/keys';
import { Box, VStack, HStack, Divider, useDisclosure, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, } from '@chakra-ui/react';
import Page from '../../components/surfaces/page';
import NavigationHeader from '../../components/navigation/navigation-header';
import Text from '../../components/content/text';
import FONTS from '../../constants/fonts';
import SearchInput from './components/inputs/search-input/index.web';
import Container from '../../components/surfaces/container';
import { useGeneralSearch } from '../../services/online/search/search';
import { useCompaniesContext } from '../../contexts/companies';
import { DocumentPreview } from '../document-viewer/document-preview/index.web';
import { useReadDocument } from '../../services/online/repository/repository';
import Icon from '../../components/content/icon';
// HTML sanitization configuration
const createSanitizedHTML = (htmlString) => {
    // First apply styling replacements
    const styledHTML = htmlString
        .replace(/<strong>/g, `<strong style="color: ${COLORS.Text.T400.value}; font-weight: 600;">`)
        .replace(/<table>/g, '<table style="border-collapse: collapse; margin: 4px 0; font-size: 11px;">')
        .replace(/<tr>/g, '<tr style="border-bottom: 1px solid #E5E7EB;">')
        .replace(/<td>/g, '<td style="padding: 2px 6px; border-right: 1px solid #E5E7EB;">')
        .replace(/<th>/g, '<th style="padding: 2px 6px; border-right: 1px solid #E5E7EB; font-weight: 600; background-color: #F3F4F6;">');
    // Then sanitize with DOMPurify allowing only safe tags and attributes
    return DOMPurify.sanitize(styledHTML, {
        ALLOWED_TAGS: [
            'strong',
            'table',
            'tr',
            'td',
            'th',
            'em',
            'i',
            'b',
            'br',
            'p',
            'span',
        ],
        ALLOWED_ATTR: ['style'],
        ALLOW_DATA_ATTR: false,
        SANITIZE_DOM: true,
        KEEP_CONTENT: true,
        FORBID_TAGS: [
            'script',
            'iframe',
            'object',
            'embed',
            'form',
            'input',
            'textarea',
            'button',
            'select',
            'option',
            'img',
        ],
        FORBID_ATTR: [
            'onclick',
            'onload',
            'onerror',
            'onmouseover',
            'onfocus',
            'onblur',
            'onchange',
            'onsubmit',
            'href',
            'src',
        ],
    });
};
const DocumentCard = ({ document, onChunkPress }) => {
    const documentResult = document.documentResult;
    if (!documentResult)
        return null;
    const hasMatchingChunks = documentResult.matchingChunks && documentResult.matchingChunks.length > 0;
    return (<Box backgroundColor={COLORS.White.T500.value} borderRadius="0.5rem" p="1rem" mb="1rem" borderWidth={1} borderColor={COLORS.Line.value}>
      <VStack spacing="0.75rem" align="stretch">
        {/* Document Header */}
        <HStack justify="space-between" align="center">
          <Box onClick={() => !hasMatchingChunks && onChunkPress(documentResult.id, 1)} cursor={!hasMatchingChunks ? 'pointer' : 'default'}>
            <VStack align="flex-start" spacing="0.25rem">
              <Text font={FONTS.T1.T16Px.Bold700.value} color={COLORS.Text.T400.value}>
                {documentResult.name || 'Document sans nom'}
              </Text>
              {documentResult.documentDate && (<Text font={FONTS.T1.T12Px.Regular400.value} color={COLORS.Text.T300.value}>
                  {new Date(documentResult.documentDate).toLocaleDateString()}
                </Text>)}
            </VStack>
          </Box>
          <Box onClick={() => !hasMatchingChunks && onChunkPress(documentResult.id, 1)} cursor={!hasMatchingChunks ? 'pointer' : 'default'}>
            <Icon name="files" width={24} height={24} color={COLORS.Text.T300.value}/>
          </Box>
        </HStack>

        {/* Matching Chunks */}
        {hasMatchingChunks && (<>
            <Divider />
            <Accordion allowToggle>
              <AccordionItem border="none">
                <AccordionButton px={0} _hover={{ bg: 'transparent' }} onClick={e => e.stopPropagation()}>
                  <Box flex="1" textAlign="left">
                    <Text font={FONTS.T1.T14Px.SemiBold600.value} color={COLORS.Text.T400.value}>
                      Correspondances trouvées (
                      {documentResult.matchingChunks.length})
                    </Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4} px={0}>
                  <VStack spacing="0.5rem" align="stretch">
                    {documentResult.matchingChunks.map((chunk, index) => (<div key={index} onClick={() => onChunkPress(documentResult.id, chunk.page || 1)} style={{
                    padding: '0.75rem',
                    backgroundColor: COLORS.BG.value,
                    borderRadius: '0.375rem',
                    borderLeft: `3px solid ${COLORS.Govrn.Blue.T500.value}`,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                }} onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor =
                        COLORS.Govrn.Blue.T50.value;
                }} onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor =
                        COLORS.BG.value;
                }}>
                          <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                }}>
                            <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '0.25rem',
                    flex: 1,
                }}>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <Text font={FONTS.T1.T12Px.SemiBold600.value} color={COLORS.Govrn.Blue.T500.value}>
                                  Page {chunk.page || 1}
                                </Text>
                              </div>
                              {chunk.highlight && (<div style={{
                        fontSize: '12px',
                        color: COLORS.Text.T300.value,
                        lineHeight: '1.4',
                    }} dangerouslySetInnerHTML={{
                        __html: createSanitizedHTML(chunk.highlight),
                    }}/>)}
                            </div>
                            <Icon name="chevron-right" width={16} height={16} color={COLORS.Govrn.Blue.T500.value}/>
                          </div>
                        </div>))}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </>)}
      </VStack>
    </Box>);
};
const Search = () => {
    // Attributes
    const { t } = useTranslation();
    const { selectedCompanyId } = useCompaniesContext();
    const [searchTerm, setSearchTerm] = useState('');
    const [viewingDocumentId, setViewingDocumentId] = useState(null);
    const [documentPreviewInitialPage, setDocumentPreviewInitialPage] = useState(null);
    const { isOpen: isDocumentViewerOpen, onOpen: onOpenDocumentViewer, onClose: onCloseDocumentViewer, } = useDisclosure();
    // Search query
    const { data: searchResults, isLoading } = useGeneralSearch({
        'company-id': selectedCompanyId || '',
        'search-term': searchTerm,
        'include-documents': true,
        'include-highlights': true,
        'max-results': 50,
    }, {
        query: {
            enabled: !!selectedCompanyId && searchTerm.length >= 3,
            staleTime: 30000, // 30 seconds
        },
    });
    // Fetch details for the currently selected document for viewing
    const { data: detailedDocumentData, isLoading: isLoadingDetailedDoc, error: detailedDocError, } = useReadDocument(viewingDocumentId, undefined, {
        query: {
            enabled: !!viewingDocumentId,
        },
    });
    // Filter only documents
    const documentResults = useMemo(() => {
        if (!searchResults)
            return [];
        return searchResults.filter(result => result.documentResult);
    }, [searchResults]);
    // Handlers
    const handleSearch = (value) => {
        setSearchTerm(value);
    };
    const handleChunkPress = (documentId, page) => {
        setViewingDocumentId(documentId);
        setDocumentPreviewInitialPage(page);
    };
    const handleCloseViewer = () => {
        setViewingDocumentId(null);
        setDocumentPreviewInitialPage(null);
    };
    // Render Item
    const renderItem = ({ item }) => {
        return <DocumentCard document={item} onChunkPress={handleChunkPress}/>;
    };
    // Render Placeholder
    const renderPlaceholder = () => {
        if (searchTerm.length < 3) {
            return (<Placeholder imageType="monocle-emoji" title={t(tKeys.search.placeholder.no_results.title)} subTitle="Tapez au moins 3 caractères pour rechercher des documents" maxWidth={320}/>);
        }
        if (!isLoading && documentResults.length === 0) {
            return (<Placeholder imageType="think-emoji" title="Aucun document trouvé" subTitle="Essayez avec d'autres mots-clés" maxWidth={210}/>);
        }
    };
    // Render
    return (<Page showSideBar barStyle="dark-content" backgroundColor={COLORS.BG.value}>
      <NavigationHeader justifyTitle="flex-start" style={{ minHeight: '3.75rem' }}>
        <Text font={FONTS.T1.T16Px.Bold700.value} color={COLORS.Text.T400.value}>
          Recherche de documents
        </Text>
      </NavigationHeader>

      <Container style={{
            marginTop: '1rem',
            marginHorizontal: 'auto',
            width: '50rem',
            maxHeight: '100%',
        }}>
        <SearchInput name="searchGlobal" value={searchTerm} onChange={handleSearch} placeholder="Rechercher dans les documents..." style={{ height: 60 }} autofocus={true}/>

        <Box mt="1rem" p="1rem" maxHeight="calc(100vh - 200px)" width="50rem" overflowY="auto" borderRadius="0.5rem" backgroundColor={COLORS.White.T500.value}>
          {/* Results Header */}
          {searchTerm.length >= 3 && (<HStack justify="space-between" align="center" mb="1rem">
              <Text font={FONTS.T1.T14Px.SemiBold600.value} color={COLORS.Text.T400.value}>
                {isLoading
                ? 'Recherche en cours...'
                : `${documentResults.length} document(s) trouvé(s)`}
              </Text>
              {isLoading && (<LoadingWrapper isLoading={true}>
                  <Box />
                </LoadingWrapper>)}
            </HStack>)}

          {/* Results List */}
          <FlatList data={documentResults} renderItem={renderItem} keyExtractor={(item, index) => `document-${item.documentResult?.id || index}`} ListEmptyComponent={renderPlaceholder} showsVerticalScrollIndicator={false}/>
        </Box>
      </Container>

      {/* Document Viewer */}
      {viewingDocumentId && (<VStack position="fixed" top={0} right={0} width="45%" height="100vh" backgroundColor={COLORS.White.T500.value} borderLeft={`1px solid ${COLORS.Line.value}`} zIndex={1000} spacing={0} align="stretch">
          <HStack justifyContent="space-between" alignItems="center" p="0.75rem" borderBottom={`1px solid ${COLORS.Line.value}`} flexShrink={0} minHeight="3rem">
            <Text font={FONTS.T1.T16Px.Bold700.value} color={COLORS.Text.T400.value}>
              {detailedDocumentData?.title ?? 'Chargement du document...'}
            </Text>
            <div onClick={handleCloseViewer} style={{
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '0.25rem',
                backgroundColor: COLORS.BG.value,
            }}>
              <Icon name="chevron-left" width={20} height={20} color={COLORS.Text.T400.value}/>
            </div>
          </HStack>

          <Box flex={1} overflow="hidden" p={isLoadingDetailedDoc || detailedDocError != null ? 4 : 0}>
            {isLoadingDetailedDoc ? (<div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}>
                <LoadingWrapper isLoading={true}>
                  <Box />
                </LoadingWrapper>
              </div>) : detailedDocError != null ? (<div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}>
                <Text font={FONTS.T1.T14Px.Regular400.value} color={COLORS.Text.T300.value}>
                  Erreur lors du chargement du document
                </Text>
              </div>) : detailedDocumentData?.file?.uri &&
                detailedDocumentData?.title ? (<DocumentPreview selectedDocument={{
                    documentUri: detailedDocumentData.file.uri,
                    documentTitle: detailedDocumentData.title,
                    document: detailedDocumentData,
                }} initialPage={documentPreviewInitialPage ?? undefined}/>) : (<div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}>
                <Text font={FONTS.T1.T14Px.Regular400.value} color={COLORS.Text.T300.value}>
                  Document non disponible pour la prévisualisation
                </Text>
              </div>)}
          </Box>
        </VStack>)}
    </Page>);
};
export default Search;
